import { useState, useRef } from "react";
import axios from "axios";
import { Upload, X, Loader2, Image as ImageIcon } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAdmin } from "../context/AdminContext";
import { toast } from "sonner";

const ImageUpload = ({ onImageUploaded, currentImages = [] }) => {
  const { getAuthHeaders, API } = useAdmin();
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate
    const allowedTypes = ["image/jpeg", "image/png", "image/webp", "image/gif"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Only JPEG, PNG, WebP and GIF images are allowed");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be less than 5MB");
      return;
    }

    // Preview
    const reader = new FileReader();
    reader.onload = (event) => setPreview(event.target.result);
    reader.readAsDataURL(file);

    // Upload
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await axios.post(`${API}/upload/image`, formData, {
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "multipart/form-data",
        },
      });

      const imageUrl = response.data.url;
      onImageUploaded(imageUrl);
      toast.success("Image uploaded successfully!");
      setPreview(null);
    } catch (error) {
      console.error("Upload failed:", error);
      toast.error(error.response?.data?.detail || "Image upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <div className="space-y-3" data-testid="image-upload">
      {/* Upload Area */}
      <div
        onClick={() => !uploading && fileInputRef.current?.click()}
        className="border-2 border-dashed border-border rounded-xl p-6 text-center cursor-pointer hover:border-primary hover:bg-primary/5 transition-all"
        data-testid="upload-dropzone"
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          onChange={handleFileSelect}
          className="hidden"
          data-testid="file-input"
        />
        
        {uploading ? (
          <div className="flex flex-col items-center gap-2">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">Uploading...</p>
          </div>
        ) : preview ? (
          <div className="relative inline-block">
            <img src={preview} alt="Preview" className="max-h-32 rounded-lg mx-auto" />
            <button
              onClick={(e) => { e.stopPropagation(); setPreview(null); }}
              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <Upload className="w-8 h-8 text-muted-foreground" />
            <p className="text-sm font-medium">Click to upload image</p>
            <p className="text-xs text-muted-foreground">JPEG, PNG, WebP, GIF (Max 5MB)</p>
          </div>
        )}
      </div>

      {/* Current Images Preview */}
      {currentImages.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {currentImages.map((img, index) => (
            <div key={index} className="w-16 h-16 rounded-lg overflow-hidden bg-muted border">
              <img src={img.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${img}` : img} alt={`Image ${index + 1}`} className="w-full h-full object-cover" />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ImageUpload;
